import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  Link,
  CircularProgress,
  Container,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { registerWithEmail, checkExistingUser } from './utilities/authUtils';
import { useFirebase } from './contexts/FirebaseContext';
import { useLocation } from 'react-router-dom';
import { collection, addDoc, Timestamp } from 'firebase/firestore';

function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { auth, db } = useFirebase();
  const location = useLocation();
  const [openDialog, setOpenDialog] = useState(false);
  const [existingAccountMethods, setExistingAccountMethods] = useState([]);

  const validateForm = () => {
    setError(null);

    if (!email || !password || !confirmPassword) {
      setError('All fields are required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const { exists, methods } = await checkExistingUser(auth, db, email);

      if (exists) {
        setExistingAccountMethods(methods);
        setOpenDialog(true);
        setLoading(false);
        return;
      }

      const { user } = await registerWithEmail(auth, db, email, password);

      await addDoc(collection(db, 'ratingRecords'), {
        userId: user.uid,
        previousRating: 50,
        newRating: 50,
        createdAt: Timestamp.now(),
        modifiedAt: Timestamp.now(),
      });

      // waglRating no longer stored - calculated on-demand from game history
      const returnUrl = location.state?.returnUrl || '/settings';

      navigate(returnUrl, {
        state: {
          message: 'Registration successful! Please verify your email address.',
        },
      });
    } catch (error) {
      console.error('Registration error:', error);

      if (error.code === 'auth/email-already-in-use') {
        setError('This email is already in use. Please try signing in instead.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else {
        setError(error.message || 'Registration failed. Please try again.');
      }

      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleNavigateToLogin = () => {
    setOpenDialog(false);
    navigate('/login', { state: { email } });
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Create an Account
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            id="confirmPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Register'}
          </Button>

          <Divider sx={{ my: 2 }} />

          <Box textAlign="center">
            <Typography variant="body2">
              Already have an account?{' '}
              <Link component={RouterLink} to="/login" variant="body2">
                Sign in
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Dialog for existing account */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Account Already Exists</DialogTitle>
        <DialogContent>
          <DialogContentText>
            An account with this email already exists. You can sign in using one of the following
            methods:
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            {existingAccountMethods.map((method, index) => {
              let methodName = '';
              switch (method) {
                case 'password':
                  methodName = 'Email and Password';
                  break;
                case 'google.com':
                  methodName = 'Google';
                  break;
                case 'phone':
                  methodName = 'Phone Number';
                  break;
                default:
                  methodName = method;
              }
              return (
                <Typography key={index} variant="body1" sx={{ mb: 1 }}>
                  • {methodName}
                </Typography>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleNavigateToLogin} variant="contained">
            Go to Sign In
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Register;
